/* global moment */

import { Dates } from '@site-module/dates.js';
import { formatter } from '@lib/formatter';

function connectLabels(i, datepicker, unified_input, single_input) {
    const label = datepicker.querySelector('label');
    if (! label) {
        return;
    }

    unified_input.id = 'unified-datepicker-' + i;
    single_input.id = 'single-datepicker-' + i;

    if (window.innerWidth < 601) {
        label.setAttribute('for', single_input.id);
    } else {
        label.setAttribute('for', unified_input.id);
    }

    window.addEventListener('resize', () => {
        if (window.innerWidth < 601) {
            label.setAttribute('for', single_input.id);
        } else {
            label.setAttribute('for', unified_input.id);
        }
    });
}

/**
 * Build daterangepicker preset ranges as Moment objects.
 *
 * @param {Moment} now - a Moment representing "current time" (passed in for testability)
 * @param {number} startOfWeekParam - integer representing the start day of the week (0=Sun, 1=Mon)
 * @param {Object} config - configuration object containing labels for ranges
 *
 * @returns {Object} - an object mapping range labels to [startMoment, endMoment] arrays
 */
export function buildRanges(now, startOfWeekParam, config) {
    const ranges = {};

    const requiredLabels = ['todayLabel', 'tomorrowLabel', 'thisweekLabel', 'nextweekLabel', 'thisweekendLabel'];
    const labelValues = new Set();
    const validLabels = {};

    for (const key of requiredLabels) {
        const value = config[key];
        if (!value || typeof value !== 'string' || value.trim() === '') {
            continue;
        }
        if (labelValues.has(value)) {
            continue;
        }
        labelValues.add(value);
        validLabels[key] = value;
    }

    if (Object.keys(validLabels).length === 0) {
        return ranges;
    }

    // default to Monday start if invalid value provided
    const startDowSafe = startOfWeekParam === 0 ? 0 : 1;

    // Date-only anchor to avoid DST/UTC off-by-one issues
    const today = now.clone().startOf('day');

    const toDateOnly = (momentObj) => momentObj.clone().startOf('day');

    function weekStart(nowDateOnly, startDow) {
        const start = toDateOnly(nowDateOnly);
        start.day(startDow);
        if (start.isAfter(nowDateOnly, 'day')) {
            start.subtract(7, 'days');
        }
        return start;
    }

    const thisWeekStart = weekStart(today, startDowSafe);
    const thisWeekEnd = toDateOnly(thisWeekStart.clone().add(6, 'days'));

    const nextWeekStart = toDateOnly(thisWeekStart.clone().add(7, 'days'));
    const nextWeekEnd = toDateOnly(nextWeekStart.clone().add(6, 'days'));

    const thisSat = toDateOnly(today.clone().isoWeekday(6));
    const thisSun = toDateOnly(thisSat.clone().add(1, 'day'));

    // If we're after Sunday, jump to next weekend.
    if (today.isAfter(thisSun, 'day')) {
        thisSat.add(7, 'days');
        thisSun.add(7, 'days');
    }

    const todayRange = [today.clone(), today.clone()];
    const tomorrowRange = [toDateOnly(today.clone().add(1, 'day')), toDateOnly(today.clone().add(1, 'day'))];
    const thisWeekendRange = [thisSat.clone(), thisSun.clone()];
    const thisWeekRange = [today.clone(), thisWeekEnd.clone()];
    const nextWeekRange = [nextWeekStart.clone(), nextWeekEnd.clone()];

    if (validLabels.todayLabel) {
        ranges[validLabels.todayLabel] = todayRange;
    }
    if (validLabels.tomorrowLabel) {
        ranges[validLabels.tomorrowLabel] = tomorrowRange;
    }
    if (validLabels.thisweekendLabel) {
        ranges[validLabels.thisweekendLabel] = thisWeekendRange;
    }
    if (validLabels.thisweekLabel) {
        ranges[validLabels.thisweekLabel] = thisWeekRange;
    }
    if (validLabels.nextweekLabel) {
        ranges[validLabels.nextweekLabel] = nextWeekRange;
    }

    return ranges;
}

export default function initDateFilters() {
    // date range filter
    document.querySelectorAll('.input-daterange').forEach((datepicker, i) => {
        // the inputs the user sees
        let unified_input = datepicker.querySelector('.input-daterange .dateRange');
        if (!unified_input) {
            return;
        }

        let single_input = datepicker.querySelector('.input-daterange .dateRangeSingle');
        // the inputs we fill with the value and send to the server
        let hidden_start_value = datepicker.querySelector('.input-daterange .startDate');
        let hidden_end_value = datepicker.querySelector('.input-daterange .endDate');

        let single_picker;
        let config = datepicker.dataset;
        let human_readable_format = formatter.php2moment(config.dateformat);

        connectLabels(i, datepicker, unified_input, single_input);

        // process event dates and ranges
        let dates = config.dates.replace(/,\s*$/, '').split(',');
        let ranges = [];
        config.ranges.replace(/,\s*$/, '').split(',').forEach(range => {
            if (range !== '') {
                ranges.push(range.split('|'));
            }
        });
        ranges.forEach(function(range) {
            // ensure the dates set has at least all the min and max values
            range.forEach(function(date) {
                if (date !== '') {
                    dates.push(date);
                }
            });
        });

        const dateHandling = new Dates(
            dates.sort(function(a, b) {
                if (a < b) {
                    return -1;
                } else if (a > b) {
                    return 1;
                }
                return 0;
            }),
            ranges
        );

        const ranges_list = buildRanges(moment(), Number(config.weekStart), config);

        if (config.birthday) {
            let this_year = moment().format('YYYY');
            let next_year = moment().add(1, 'years').format('YYYY');
            let birthday = moment(config.birthday).format('-MM-DD');
            let bday_this = moment(this_year + birthday);
            let bday_next = moment(next_year + birthday);

            if (bday_this.isSameOrAfter(moment())) {
                ranges_list[config.birthdayLabel] = [bday_this, bday_this];
            } else {
                ranges_list[config.birthdayLabel] = [bday_next, bday_next];
            }
        }
        if (config.customRange) {
            let period = config.customRange.split(',');
            ranges_list[period[0]] = [moment(period[1]), moment(period[2])];
        }

        const shared_config = {
            alwaysShowCalendars: true,
            showCustomRangeLabel: false,
            autoApply: config.autosubmit === 'true' ? true : false,
            autoUpdateInput: false,
            parentEl: 'body',
            startDate: dateHandling.getSelectedDate(hidden_start_value),
            endDate: dateHandling.getSelectedDate(hidden_end_value),
            minDate: dateHandling.getMinDate(config.showHistory),
            maxDate: dateHandling.getMaxDate(),
            isCustomDate: d => {
                // expects a function that returns a class name for each date
                return dateHandling.getDateClassnames(d);
            },
            locale: {
                format: human_readable_format,
                separator: ' ' + config.separator + ' ',
                firstDay: Number(config.weekStart),
                applyLabel: config.applyLabel,
                cancelLabel: config.cancelLabel
            },
            ranges: ranges_list
        };

        function updateDatepickerStyle() {
            if (config.style) {
                if (datepicker.querySelector('.daterangepicker')) {
                    datepicker.querySelector('.daterangepicker').classList.add(config.style);
                }
                if (config.style === 'simple') {
                    shared_config['opens'] = 'left';
                }
            }
        }

        // Add custom style class to datepicker
        updateDatepickerStyle();

        function syncValues(start, end) {
            if (end) {
                unified_input.value = start.format(human_readable_format) + ' ' + config.separator + ' ' + end.format(human_readable_format);
                single_input.value = start.format(human_readable_format) + ' ' + config.separator + ' ' + end.format(human_readable_format);
            } else {
                unified_input.value = config.startrangeLabel + ' ' + start.format(human_readable_format);
                single_input.value = config.startrangeLabel + ' ' + start.format(human_readable_format);
            }
            hidden_start_value.value = start.format('YYYY-MM-DD');
            hidden_end_value.value = end ? end.format('YYYY-MM-DD') : null;
            single_picker.data('daterangepicker').setStartDate(start);
            single_picker.data('daterangepicker').setEndDate(end);
            $('.submitFilters').removeAttr('disabled');
            unified_input.form.submit();
        }

        // prevent the keyboard from opening on mobile devices
        unified_input.addEventListener('click', function(event) {
            updateDatepickerStyle();
            event.target.blur();
        });
        single_input.addEventListener('click', function(event) {
            updateDatepickerStyle();
            event.target.blur();
        });

        $(unified_input).daterangepicker($.extend(shared_config, {
            linkedCalendars: true
        }), function(start, end) {
            syncValues(start, end);
        });

        $(unified_input).on('apply.daterangepicker', function(ev, picker) {
            syncValues(picker.startDate, picker.endDate);
        });

        single_picker = $(single_input).daterangepicker($.extend(shared_config, {
            singleDatePicker: true,
            singleRangePicker: true,
            endDateFallback: dateHandling.getMaxDate()
        }), function(start, end) {
            syncValues(start, end);
        });
    });
}
